// src/pages/admin/UserAccessManagement.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  documentId,
  endAt,
  getDocs,
  limit,
  orderBy,
  query,
  startAt,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase";
import useAccessProfile from "../../hooks/useAccessProfile";
import { canManageRoles } from "../../utils/accessControl";
import "./UserAccessManagement.css";

const ROLE_OPTIONS = [
  {
    key: "admin",
    label: "Admin",
    description: "Internal NexterZone administrator.",
    internalOnly: true,
  },
  {
    key: "finance",
    label: "Finance",
    description: "Finance dashboard and reporting access. Requires Admin.",
    internalOnly: true,
  },
  {
    key: "inspector",
    label: "Inspector",
    description: "Internal NexterZone inspector.",
    internalOnly: true,
  },
  {
    key: "partner_inspector",
    label: "Partner Inspector",
    description: "External partner/store inspector.",
    internalOnly: false,
  },
];

const PERMISSION_OPTIONS = [
  {
    key: "sellerPrepApproval",
    label: "Seller Prep Approval",
    description: "Review and approve seller evidence.",
    group: "Workflow",
  },
  {
    key: "tradeIssueManagement",
    label: "Trade Issue Management",
    description: "Handle disputes, exceptions, and final completion.",
    group: "Exceptions",
  },
  {
    key: "inspectionReview",
    label: "Inspection Review",
    description: "Perform intake, pickup, and inspection actions.",
    group: "Inspection",
  },
  {
    key: "pickupManagement",
    label: "Pickup Management",
    description: "Schedule pickup and start custody transfer.",
    group: "Logistics",
  },
  {
    key: "deliveryManagement",
    label: "Delivery Management",
    description: "Schedule delivery and mark delivered.",
    group: "Logistics",
  },
  {
    key: "roleManagement",
    label: "Role Management",
    description: "Manage user roles and permissions.",
    group: "Administration",
  },
  {
    key: "financeReporting",
    label: "Finance Reporting",
    description: "View finance reports and revenue summaries.",
    group: "Finance",
  },
];

const DEFAULT_FORM = {
  targetUid: "",
  roles: [],
  permissions: {
    sellerPrepApproval: false,
    tradeIssueManagement: false,
    inspectionReview: false,
    pickupManagement: false,
    deliveryManagement: false,
    roleManagement: false,
    financeReporting: false,
  },
  staffProfile: {
    inspectorType: null,
    partnerId: "",
    storeId: "",
    organizationType: "",
  },
  isActive: true,
};

const PARTNER_COLLECTION_CANDIDATES = [
  "partners",
  "Partners",
  "approvedPartners",
  "ApprovedPartners",
];

const STORE_COLLECTION_CANDIDATES = [
  "stores",
  "Stores",
  "dropoffPoints",
  "DropoffPoints",
  "approvedDropoffPoints",
  "ApprovedDropoffPoints",
];

function safeString(value) {
  return typeof value === "string" ? value.trim() : "";
}

function lower(value) {
  return safeString(value).toLowerCase();
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const s = safeString(value);
    if (s) return s;
  }
  return "";
}

function isNexterzoneEmail(email) {
  return lower(email).endsWith("@nexterzone.com");
}

function hasInternalRole(roles = []) {
  return roles.some((role) => ["admin", "finance", "inspector"].includes(role));
}

function toUserRow(docSnap) {
  const data = docSnap.data() || {};
  const email = safeString(data.email);
  const uid = safeString(data.uid || docSnap.id);

  return {
    id: docSnap.id,
    ...data,
    uid,
    email,
    emailLower: safeString(data.emailLower || email.toLowerCase()),
    displayName: safeString(data.displayName),
    roles: Array.isArray(data.roles) ? data.roles : [],
    permissions:
      data.permissions && typeof data.permissions === "object"
        ? data.permissions
        : {},
    staffProfile:
      data.staffProfile && typeof data.staffProfile === "object"
        ? {
            inspectorType: data.staffProfile.inspectorType || null,
            partnerId: data.staffProfile.partnerId || "",
            storeId: data.staffProfile.storeId || "",
            organizationType: data.staffProfile.organizationType || "",
          }
        : {
            inspectorType: null,
            partnerId: "",
            storeId: "",
            organizationType: "",
          },
    isActive: data.isActive !== false,
    updatedAt: data.updatedAt || null,
    updatedByEmail: data.updatedByEmail || "",
  };
}

function toForm(user = {}) {
  return {
    targetUid: user.uid || "",
    roles: Array.isArray(user.roles) ? user.roles : [],
    permissions: {
      ...DEFAULT_FORM.permissions,
      ...(user.permissions || {}),
    },
    staffProfile: {
      inspectorType: user.staffProfile?.inspectorType || null,
      partnerId: user.staffProfile?.partnerId || "",
      storeId: user.staffProfile?.storeId || "",
      organizationType: user.staffProfile?.organizationType || "",
    },
    isActive: user.isActive !== false,
  };
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    let date = null;
    if (typeof value?.toDate === "function") date = value.toDate();
    else if (value instanceof Date) date = value;
    else date = new Date(value);

    if (!date || Number.isNaN(date.getTime())) return "—";

    return new Intl.DateTimeFormat("en-ZA", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  } catch {
    return "—";
  }
}

function formatList(items = []) {
  return Array.isArray(items) && items.length ? items.join(", ") : "None";
}

function normalizePermissions(obj = {}) {
  return {
    sellerPrepApproval: !!obj.sellerPrepApproval,
    tradeIssueManagement: !!obj.tradeIssueManagement,
    inspectionReview: !!obj.inspectionReview,
    pickupManagement: !!obj.pickupManagement,
    deliveryManagement: !!obj.deliveryManagement,
    roleManagement: !!obj.roleManagement,
    financeReporting: !!obj.financeReporting,
  };
}

function permissionsToEnabledList(permissions = {}) {
  return Object.entries(normalizePermissions(permissions))
    .filter(([, enabled]) => enabled)
    .map(([key]) => key);
}

function roleBadgeClass(role) {
  if (role === "admin") return "uam-chip uam-chip--admin";
  if (role === "finance") return "uam-chip uam-chip--finance";
  if (role === "inspector") return "uam-chip uam-chip--inspector";
  if (role === "partner_inspector") return "uam-chip uam-chip--partner";
  return "uam-chip";
}

function getInitials(user) {
  const base =
    safeString(user?.displayName) ||
    safeString(user?.email)?.split("@")[0] ||
    safeString(user?.uid);

  if (!base) return "U";

  const parts = base.replace(/[_-]+/g, " ").split(" ").filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function getDisplayName(user) {
  return (
    safeString(user?.displayName) ||
    safeString(user?.email) ||
    safeString(user?.uid) ||
    "Unknown user"
  );
}

function buildPartnerOption(doc) {
  const data = doc.data() || {};
  const id = data.partnerId || data.id || doc.id;
  const name =
    firstNonEmpty(data.partnerName, data.name, data.displayName, data.title) ||
    id;

  return { id, label: `${name} (${id})`, raw: data };
}

function buildStoreOption(doc) {
  const data = doc.data() || {};
  const id = data.storeId || data.id || doc.id;
  const partnerId = firstNonEmpty(
    data.partnerId,
    data.partner?.id,
    data.ownerPartnerId
  );

  const name =
    firstNonEmpty(data.storeName, data.name, data.displayName, data.title) ||
    id;

  const city = firstNonEmpty(data.city, data.addressCity, data.locationCity);
  const address = firstNonEmpty(data.address, data.line1, data.locationName);

  return {
    id,
    partnerId,
    label: city ? `${name} (${id}) • ${city}` : `${name} (${id})`,
    subtitle: address || "",
    raw: data,
  };
}

function sortOptions(options = []) {
  return [...options].sort((a, b) =>
    String(a.label || "").localeCompare(String(b.label || ""))
  );
}

async function fetchFirstExistingCollection(collectionNames, mapper) {
  for (const name of collectionNames) {
    try {
      const snap = await getDocs(query(collection(db, name), limit(300)));

      // Important: only return when the collection actually has records.
      if (!snap.empty) {
        return snap.docs.map(mapper);
      }

      console.info(`Collection '${name}' exists but has no records. Checking next candidate...`);
    } catch (err) {
      console.warn(`Skipping collection '${name}'`, err);
    }
  }

  return [];
}

function Section({ title, eyebrow, count, open, onToggle, children }) {
  return (
    <section className={`uam-panel uam-collapse ${open ? "is-open" : ""}`}>
      <button type="button" className="uam-collapse-head" onClick={onToggle}>
        <span>
          {eyebrow ? <span className="uam-panel-kicker">{eyebrow}</span> : null}
          <strong>{title}</strong>
        </span>
        <span className="uam-collapse-side">
          {count != null ? <span className="uam-mini-pill">{count}</span> : null}
          <span className="uam-chevron">{open ? "−" : "+"}</span>
        </span>
      </button>

      {open ? <div className="uam-collapse-body">{children}</div> : null}
    </section>
  );
}

function UserCard({ user, selected, onClick, compact = false }) {
  return (
    <button
      type="button"
      className={`uam-user-card ${selected ? "is-selected" : ""} ${
        compact ? "is-compact" : ""
      }`}
      onClick={onClick}
    >
      <div className="uam-user-top">
        <div className="uam-user-avatar">{getInitials(user)}</div>

        <div className="uam-user-main">
          <div className="uam-user-name">{getDisplayName(user)}</div>
          <div className="uam-user-email">
            {user.email || user.emailLower || "No email on profile"}
          </div>
          <div className="uam-user-meta">
            {compact ? `Updated: ${formatDateTime(user.updatedAt)}` : `UID: ${user.uid}`}
          </div>
        </div>

        <div
          className={`uam-state ${
            user.isActive !== false ? "is-active" : "is-inactive"
          }`}
        >
          {user.isActive !== false ? "Active" : "Inactive"}
        </div>
      </div>

      <div className="uam-badges">
        {user.roles?.length ? (
          user.roles.map((r) => (
            <span key={r} className={roleBadgeClass(r)}>
              {r}
            </span>
          ))
        ) : (
          <span className="uam-chip uam-chip--muted">No role</span>
        )}
      </div>
    </button>
  );
}

export default function UserAccessManagement() {
  const { profile, loading: accessLoading } = useAccessProfile();

  const [accessMode, setAccessMode] = useState("internal");
  const [queryText, setQueryText] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchStatus, setSearchStatus] = useState("");

  const [recentUsers, setRecentUsers] = useState([]);
  const [recentBusy, setRecentBusy] = useState(true);

  const [selectedUser, setSelectedUser] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyStatus, setHistoryStatus] = useState("");

  const [partnerOptions, setPartnerOptions] = useState([]);
  const [storeOptions, setStoreOptions] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupStatus, setLookupStatus] = useState("");

  const [openSection, setOpenSection] = useState("roles");

  const selectedEmail = selectedUser?.email || selectedUser?.emailLower || "";
  const selectedIsInternal = isNexterzoneEmail(selectedEmail);

  const permissionGroups = useMemo(() => {
    const map = new Map();
    for (const p of PERMISSION_OPTIONS) {
      if (!map.has(p.group)) map.set(p.group, []);
      map.get(p.group).push(p);
    }
    return Array.from(map.entries());
  }, []);

  const summaryEnabledPermissions = useMemo(
    () => permissionsToEnabledList(form.permissions),
    [form.permissions]
  );

  const selectedPartner = useMemo(
    () => partnerOptions.find((p) => p.id === form.staffProfile.partnerId) || null,
    [partnerOptions, form.staffProfile.partnerId]
  );

  const storesForSelectedPartner = useMemo(() => {
    if (!form.staffProfile.partnerId) return storeOptions;

    const filtered = storeOptions.filter((store) => {
      if (!store.partnerId) return true;
      return store.partnerId === form.staffProfile.partnerId;
    });

    return filtered.length ? filtered : storeOptions;
  }, [storeOptions, form.staffProfile.partnerId]);

  const selectedStore = useMemo(
    () =>
      storesForSelectedPartner.find(
        (item) => item.id === form.staffProfile.storeId
      ) || null,
    [storesForSelectedPartner, form.staffProfile.storeId]
  );

  const isPartnerMode = useMemo(
    () =>
      form.roles.includes("partner_inspector") ||
      form.staffProfile.inspectorType === "partner",
    [form.roles, form.staffProfile.inspectorType]
  );

  const saveBlockedReason = useMemo(() => {
    if (!selectedUser) return "Select a user first.";

    if (hasInternalRole(form.roles) && !selectedIsInternal) {
      return "Internal roles such as Admin, Finance, and Inspector can only be assigned to @nexterzone.com email accounts.";
    }

    if (form.roles.includes("partner_inspector")) {
      if (!form.permissions.inspectionReview) {
        return "Partner inspectors must have Inspection Review permission enabled.";
      }

      if (!form.staffProfile.partnerId) {
        return "Partner inspectors must be linked to a partner.";
      }
    }

    if (form.roles.includes("finance") && !form.roles.includes("admin")) {
      return "Finance access must be assigned together with Admin.";
    }

    return "";
  }, [selectedUser, form, selectedIsInternal]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery(queryText.trim().toLowerCase());
    }, 250);
    return () => clearTimeout(t);
  }, [queryText]);

  useEffect(() => {
    if (!selectedUser) {
      setForm(DEFAULT_FORM);
      setHistory([]);
      setHistoryStatus("");
      return;
    }

    setForm(toForm(selectedUser));
  }, [selectedUser]);

  useEffect(() => {
    setSearchResults([]);
    setSearchStatus("");
    setSelectedUser(null);
    setQueryText("");
  }, [accessMode]);

  useEffect(() => {
    const partnerMode =
      form.roles.includes("partner_inspector") ||
      form.staffProfile.inspectorType === "partner";

    if (!partnerMode) return;

    if (!form.staffProfile.partnerId) {
      setForm((prev) => ({
        ...prev,
        staffProfile: {
          ...prev.staffProfile,
          storeId: "",
        },
      }));
      return;
    }

    const selectedStoreStillValid = storesForSelectedPartner.some(
      (store) => store.id === form.staffProfile.storeId
    );

    if (!selectedStoreStillValid && form.staffProfile.storeId && storeOptions.length) {
      setForm((prev) => ({
        ...prev,
        staffProfile: {
          ...prev.staffProfile,
          storeId: "",
        },
      }));
    }
  }, [
    form.roles,
    form.staffProfile.inspectorType,
    form.staffProfile.partnerId,
    form.staffProfile.storeId,
    storesForSelectedPartner,
    storeOptions.length,
  ]);

  async function loadPartnerStoreLookups() {
    setLookupLoading(true);
    setLookupStatus("");

    try {
      const partners = await fetchFirstExistingCollection(
        PARTNER_COLLECTION_CANDIDATES,
        buildPartnerOption
      );

      const stores = await fetchFirstExistingCollection(
        STORE_COLLECTION_CANDIDATES,
        buildStoreOption
      );

      const dedupedPartnersMap = new Map();
      for (const p of partners) {
        if (!p?.id) continue;
        if (!dedupedPartnersMap.has(p.id)) dedupedPartnersMap.set(p.id, p);
      }

      const dedupedStoresMap = new Map();
      for (const s of stores) {
        if (!s?.id) continue;
        if (!dedupedStoresMap.has(s.id)) dedupedStoresMap.set(s.id, s);
      }

      setPartnerOptions(sortOptions(Array.from(dedupedPartnersMap.values())));
      setStoreOptions(sortOptions(Array.from(dedupedStoresMap.values())));

      if (!partners.length && !stores.length) {
        setLookupStatus(
          "No partner/store lookup records found. You can still enter partner and store IDs manually."
        );
      }
    } catch (err) {
      console.error(err);
      setLookupStatus("Failed to load partner/store lookup data.");
      setPartnerOptions([]);
      setStoreOptions([]);
    } finally {
      setLookupLoading(false);
    }
  }

  async function loadRecentUsers() {
    setRecentBusy(true);
    try {
      const snap = await getDocs(
        query(collection(db, "users"), orderBy("updatedAt", "desc"), limit(8))
      );

      const rows = snap.docs.map(toUserRow);

      setRecentUsers(
        rows.filter((user) => {
          if (accessMode === "internal") {
            return isNexterzoneEmail(user.email || user.emailLower);
          }
          return true;
        })
      );
    } catch (err) {
      console.error("loadRecentUsers failed:", err);
      setRecentUsers([]);
    } finally {
      setRecentBusy(false);
    }
  }

  async function loadHistory(uid) {
    if (!uid) {
      setHistory([]);
      setHistoryStatus("");
      return;
    }

    setHistoryLoading(true);
    setHistoryStatus("");

    try {
      const historyRef = collection(db, "users", uid, "accessHistory");
      const q = query(historyRef, orderBy("changedAt", "desc"), limit(20));
      const snap = await getDocs(q);
      setHistory(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
      setHistory([]);
      setHistoryStatus("Failed to load access history.");
    } finally {
      setHistoryLoading(false);
    }
  }

  async function runSearch(raw) {
    const qText = raw.trim().toLowerCase();

    setSearchResults([]);
    setSearchStatus("");

    if (!qText) return;

    setSearchBusy(true);

    try {
      const resultsMap = new Map();

      const uidPromise = getDocs(
        query(collection(db, "users"), where(documentId(), "==", qText), limit(1))
      );

      const emailPromise = getDocs(
        query(
          collection(db, "users"),
          orderBy("emailLower"),
          startAt(qText),
          endAt(`${qText}\uf8ff`),
          limit(20)
        )
      );

      const [uidSnap, emailSnap] = await Promise.all([uidPromise, emailPromise]);

      uidSnap.docs.forEach((docSnap) => {
        const row = toUserRow(docSnap);
        resultsMap.set(row.uid, row);
      });

      emailSnap.docs.forEach((docSnap) => {
        const row = toUserRow(docSnap);
        resultsMap.set(row.uid, row);
      });

      let rows = Array.from(resultsMap.values());

      if (accessMode === "internal") {
        rows = rows.filter((user) =>
          isNexterzoneEmail(user.email || user.emailLower)
        );
      }

      rows.sort((a, b) => {
        const aKey = a.emailLower || a.email || a.uid || "";
        const bKey = b.emailLower || b.email || b.uid || "";
        return String(aKey).localeCompare(String(bKey));
      });

      setSearchResults(rows);

      if (!rows.length) {
        setSearchStatus(
          accessMode === "internal"
            ? "No NexterZone staff account matched that search. Internal roles require an @nexterzone.com email."
            : "No user matched that search. The partner inspector must sign in first so a user document exists."
        );
      }
    } catch (err) {
      console.error("runSearch failed:", err);
      setSearchResults([]);
      setSearchStatus(err?.message || "Failed to search users.");
    } finally {
      setSearchBusy(false);
    }
  }

  useEffect(() => {
    loadPartnerStoreLookups();
  }, []);

  useEffect(() => {
    loadRecentUsers();
  }, [accessMode]);

  useEffect(() => {
    if (!debouncedQuery) {
      setSearchResults([]);
      setSearchStatus("");
      return;
    }

    runSearch(debouncedQuery);
  }, [debouncedQuery, accessMode]);

  async function pickUser(user) {
    setSelectedUser(user);
    setStatus("");

    if (accessMode === "partner") {
      setForm((prev) => ({
        ...toForm(user),
        roles: ["partner_inspector"],
        permissions: {
          ...DEFAULT_FORM.permissions,
          inspectionReview: true,
        },
        staffProfile: {
          inspectorType: "partner",
          partnerId: prev.staffProfile.partnerId || "",
          storeId: prev.staffProfile.storeId || "",
          organizationType: "partner",
        },
        isActive: user.isActive !== false,
      }));
      setOpenSection("staff");
    } else {
      setForm(toForm(user));
      setOpenSection("roles");
    }

    await loadHistory(user.uid);
  }

  function toggleRole(role) {
    if (!selectedUser) return;

    const roleConfig = ROLE_OPTIONS.find((r) => r.key === role);
    const email = selectedUser.email || selectedUser.emailLower || "";
    const isInternalUser = isNexterzoneEmail(email);

    if (roleConfig?.internalOnly && !isInternalUser) {
      setStatus(
        "This is not a NexterZone email account. Assign Partner Inspector access instead."
      );
      setAccessMode("partner");
      return;
    }

    setStatus("");

    setForm((prev) => {
      const exists = prev.roles.includes(role);
      let nextRoles = exists
        ? prev.roles.filter((r) => r !== role)
        : [...prev.roles, role];

      if (role === "finance" && !exists && !nextRoles.includes("admin")) {
        nextRoles = ["admin", ...nextRoles];
      }

      if (role === "admin" && exists) {
        nextRoles = nextRoles.filter((r) => r !== "finance");
      }

      if (role === "inspector" && !exists) {
        nextRoles = nextRoles.filter((r) => r !== "partner_inspector");
      }

      if (role === "partner_inspector" && !exists) {
        nextRoles = nextRoles.filter((r) => !["admin", "finance", "inspector"].includes(r));
      }

      let nextPermissions = { ...prev.permissions };
      let nextInspectorType = prev.staffProfile.inspectorType;
      let nextPartnerId = prev.staffProfile.partnerId;
      let nextStoreId = prev.staffProfile.storeId;
      let nextOrganizationType = prev.staffProfile.organizationType;

      if (nextRoles.includes("partner_inspector")) {
        nextInspectorType = "partner";
        nextOrganizationType = "partner";
        nextPermissions.inspectionReview = true;
      } else if (nextRoles.includes("inspector")) {
        nextInspectorType = "internal";
        nextOrganizationType = "internal";
        nextPartnerId = "";
        nextStoreId = "";
        nextPermissions.inspectionReview = true;
      } else {
        nextInspectorType = null;
        nextOrganizationType = "";
        nextPartnerId = "";
        nextStoreId = "";
      }

      if (nextRoles.includes("finance")) {
        nextPermissions.financeReporting = true;
      }

      return {
        ...prev,
        roles: nextRoles,
        permissions: nextPermissions,
        staffProfile: {
          ...prev.staffProfile,
          inspectorType: nextInspectorType,
          partnerId: nextPartnerId,
          storeId: nextStoreId,
          organizationType: nextOrganizationType,
        },
      };
    });
  }

  function togglePermission(key) {
    setForm((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: !prev.permissions[key],
      },
    }));
  }

  function applyPreset(type) {
    if (!selectedUser) return;

    const email = selectedUser.email || selectedUser.emailLower || "";
    const isInternalUser = isNexterzoneEmail(email);

    if (
      ["full_admin", "finance_admin", "ops_admin", "internal_inspector"].includes(type) &&
      !isInternalUser
    ) {
      setStatus(
        "Internal presets require an @nexterzone.com email. Use Partner Inspector for external partner users."
      );
      setAccessMode("partner");
      return;
    }

    setStatus("");

    if (type === "full_admin") {
      setForm((prev) => ({
        ...prev,
        roles: ["admin"],
        permissions: {
          sellerPrepApproval: true,
          tradeIssueManagement: true,
          inspectionReview: true,
          pickupManagement: true,
          deliveryManagement: true,
          roleManagement: true,
          financeReporting: false,
        },
        staffProfile: DEFAULT_FORM.staffProfile,
        isActive: true,
      }));
      return;
    }

    if (type === "finance_admin") {
      setForm((prev) => ({
        ...prev,
        roles: ["admin", "finance"],
        permissions: {
          ...DEFAULT_FORM.permissions,
          financeReporting: true,
        },
        staffProfile: DEFAULT_FORM.staffProfile,
        isActive: true,
      }));
      return;
    }

    if (type === "ops_admin") {
      setForm((prev) => ({
        ...prev,
        roles: ["admin"],
        permissions: {
          sellerPrepApproval: true,
          tradeIssueManagement: true,
          inspectionReview: true,
          pickupManagement: true,
          deliveryManagement: true,
          roleManagement: false,
          financeReporting: false,
        },
        staffProfile: DEFAULT_FORM.staffProfile,
        isActive: true,
      }));
      return;
    }

    if (type === "internal_inspector") {
      setForm((prev) => ({
        ...prev,
        roles: ["inspector"],
        permissions: {
          ...DEFAULT_FORM.permissions,
          inspectionReview: true,
        },
        staffProfile: {
          inspectorType: "internal",
          partnerId: "",
          storeId: "",
          organizationType: "internal",
        },
        isActive: true,
      }));
      return;
    }

    if (type === "partner_inspector") {
      setAccessMode("partner");
      setForm((prev) => ({
        ...prev,
        roles: ["partner_inspector"],
        permissions: {
          ...DEFAULT_FORM.permissions,
          inspectionReview: true,
        },
        staffProfile: {
          inspectorType: "partner",
          partnerId: prev.staffProfile.partnerId || "",
          storeId: prev.staffProfile.storeId || "",
          organizationType: "partner",
        },
        isActive: true,
      }));
      setOpenSection("staff");
      return;
    }

    if (type === "deactivate") {
      setForm((prev) => ({ ...prev, isActive: false }));
    }
  }

  function resetForm() {
    if (!selectedUser) return;
    setForm(toForm(selectedUser));
    setStatus("Changes reset.");
  }

  async function reloadSelectedUser() {
    if (!selectedUser?.uid) return;

    try {
      const snap = await getDocs(
        query(collection(db, "users"), where(documentId(), "==", selectedUser.uid), limit(1))
      );

      if (!snap.empty) {
        const fresh = toUserRow(snap.docs[0]);
        setSelectedUser(fresh);
      }
    } catch (err) {
      console.error("reloadSelectedUser failed:", err);
    }
  }

  async function onSave(e) {
    e.preventDefault();
    setStatus("");

    if (saveBlockedReason) {
      setStatus(saveBlockedReason);
      return;
    }

    setSaving(true);

    try {
      const callable = httpsCallable(functions, "setUserAccessProfile");

      const res = await callable({
        targetUid: form.targetUid,
        roles: form.roles,
        permissions: form.permissions,
        staffProfile: {
          inspectorType: form.staffProfile.inspectorType || null,
          partnerId: form.staffProfile.partnerId || null,
          storeId: form.staffProfile.storeId || null,
          organizationType: form.staffProfile.organizationType || null,
        },
        isActive: form.isActive,
      });

      await reloadSelectedUser();
      await loadHistory(form.targetUid);
      await loadRecentUsers();

      if (debouncedQuery) {
        await runSearch(debouncedQuery);
      }

      setStatus(res?.data?.message || "Access profile saved successfully.");
    } catch (err) {
      console.error(err);
      setStatus(err?.message || "Failed to save access profile.");
    } finally {
      setSaving(false);
    }
  }

  if (accessLoading) {
    return (
      <div className="uam-shell">
        <div className="uam-empty">Loading access profile...</div>
      </div>
    );
  }

  if (!canManageRoles(profile)) {
    return (
      <div className="uam-shell">
        <div className="uam-empty">
          You do not have permission to manage user access.
        </div>
      </div>
    );
  }

  return (
    <div className="uam-shell">
      <section className="uam-header">
        <div>
          <div className="uam-eyebrow">Admin Console</div>
          <h1>User Access</h1>
          <p>Search one user, assign access, save. Built for scale.</p>
        </div>

        <div className="uam-header-actions">
          <span className="uam-admin-pill">
            <span className="uam-admin-dot" />
            {profile?.email || "admin"}
          </span>

          <button
            type="button"
            className="uam-btn uam-btn--soft"
            onClick={loadPartnerStoreLookups}
            disabled={lookupLoading}
          >
            {lookupLoading ? "Refreshing..." : "Refresh Lookups"}
          </button>
        </div>
      </section>

      {(lookupStatus || status || searchStatus) && (
        <div className="uam-top-status-wrap">
          {lookupStatus ? <div className="uam-status">{lookupStatus}</div> : null}
          {searchStatus ? <div className="uam-status">{searchStatus}</div> : null}
          {status ? <div className="uam-status">{status}</div> : null}
        </div>
      )}

      <div className="uam-layout">
        <aside className="uam-sidebar">
          <div className="uam-panel uam-search-panel">
            <div className="uam-panel-headline">
              <div>
                <div className="uam-panel-kicker">Directory</div>
                <div className="uam-panel-title">Find User</div>
              </div>
              <div className="uam-mini-pill">
                {searchBusy ? "Searching" : `${searchResults.length}`}
              </div>
            </div>

            <div className="uam-mode-switch">
              <button
                type="button"
                className={accessMode === "internal" ? "is-active" : ""}
                onClick={() => setAccessMode("internal")}
              >
                Internal Staff
              </button>
              <button
                type="button"
                className={accessMode === "partner" ? "is-active" : ""}
                onClick={() => setAccessMode("partner")}
              >
                Partner Inspector
              </button>
            </div>

            <div className="uam-search-wrap">
              <input
                className="uam-search"
                placeholder={
                  accessMode === "internal"
                    ? "Search @nexterzone.com email or exact UID"
                    : "Search partner email or exact UID"
                }
                value={queryText}
                onChange={(e) => setQueryText(e.target.value)}
              />
            </div>

            <div className="uam-helper">
              {accessMode === "internal"
                ? "Internal roles are restricted to verified NexterZone staff accounts."
                : "Partner inspector access can be assigned to any registered user email."}
            </div>

            <div className="uam-user-list">
              {!queryText.trim() ? (
                <div className="uam-empty">Start typing to search.</div>
              ) : searchBusy ? (
                <div className="uam-empty">Searching users...</div>
              ) : searchResults.length === 0 ? (
                <div className="uam-empty">No matching users found.</div>
              ) : (
                searchResults.map((u) => (
                  <UserCard
                    key={u.uid}
                    user={u}
                    selected={selectedUser?.uid === u.uid}
                    onClick={() => pickUser(u)}
                  />
                ))
              )}
            </div>
          </div>

          <div className="uam-panel uam-recent-panel">
            <div className="uam-panel-headline">
              <div>
                <div className="uam-panel-kicker">Recent</div>
                <div className="uam-panel-title">Managed Users</div>
              </div>

              <button
                type="button"
                className="uam-icon-btn"
                onClick={loadRecentUsers}
                disabled={recentBusy}
                title="Refresh recent users"
              >
                ↻
              </button>
            </div>

            <div className="uam-user-list">
              {recentBusy ? (
                <div className="uam-empty">Loading recent users...</div>
              ) : recentUsers.length === 0 ? (
                <div className="uam-empty">No recent users.</div>
              ) : (
                recentUsers.map((u) => (
                  <UserCard
                    compact
                    key={`recent_${u.uid}`}
                    user={u}
                    selected={selectedUser?.uid === u.uid}
                    onClick={() => pickUser(u)}
                  />
                ))
              )}
            </div>
          </div>
        </aside>

        <main className="uam-main">
          {!selectedUser ? (
            <div className="uam-panel uam-empty-panel">
              <div className="uam-empty">
                Select a user to manage roles, permissions, and partner assignment.
              </div>
            </div>
          ) : (
            <form className="uam-form" onSubmit={onSave}>
              <section className="uam-selected-card uam-panel">
                <div className="uam-selected-left">
                  <div className="uam-selected-avatar">{getInitials(selectedUser)}</div>

                  <div>
                    <div className="uam-panel-kicker">Selected User</div>
                    <div className="uam-selected-name">
                      {getDisplayName(selectedUser)}
                    </div>
                    <div className="uam-selected-meta">
                      {selectedEmail || "No email"} • UID: {selectedUser.uid}
                    </div>

                    <div className="uam-badges uam-selected-badges">
                      <span
                        className={`uam-state ${
                          form.isActive ? "is-active" : "is-inactive"
                        }`}
                      >
                        {form.isActive ? "Active" : "Inactive"}
                      </span>

                      {selectedIsInternal ? (
                        <span className="uam-chip uam-chip--admin">
                          NexterZone Staff
                        </span>
                      ) : (
                        <span className="uam-chip uam-chip--partner">
                          External / Partner
                        </span>
                      )}

                      {form.roles.length ? (
                        form.roles.map((r) => (
                          <span key={r} className={roleBadgeClass(r)}>
                            {r}
                          </span>
                        ))
                      ) : (
                        <span className="uam-chip uam-chip--muted">No role</span>
                      )}
                    </div>
                  </div>
                </div>

                <label className="uam-toggle">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        isActive: e.target.checked,
                      }))
                    }
                  />
                  <span />
                  <strong>{form.isActive ? "Active" : "Inactive"}</strong>
                </label>
              </section>

              <section className="uam-panel uam-presets-panel">
                <div className="uam-panel-headline">
                  <div>
                    <div className="uam-panel-kicker">Shortcuts</div>
                    <div className="uam-panel-title">Apply Preset</div>
                  </div>
                </div>

                <div className="uam-presets-compact">
                  <button type="button" onClick={() => applyPreset("full_admin")}>
                    Full Admin
                  </button>
                  <button type="button" onClick={() => applyPreset("finance_admin")}>
                    Finance Admin
                  </button>
                  <button type="button" onClick={() => applyPreset("ops_admin")}>
                    Ops Admin
                  </button>
                  <button type="button" onClick={() => applyPreset("internal_inspector")}>
                    Internal Inspector
                  </button>
                  <button type="button" onClick={() => applyPreset("partner_inspector")}>
                    Partner Inspector
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => applyPreset("deactivate")}
                  >
                    Deactivate
                  </button>
                </div>
              </section>

              <Section
                eyebrow="Access Layer"
                title="Roles"
                count={form.roles.length}
                open={openSection === "roles"}
                onToggle={() =>
                  setOpenSection(openSection === "roles" ? "" : "roles")
                }
              >
                <div className="uam-role-grid">
                  {ROLE_OPTIONS.map((role) => {
                    const selected = form.roles.includes(role.key);
                    const blocked = role.internalOnly && !selectedIsInternal;

                    return (
                      <button
                        key={role.key}
                        type="button"
                        className={`uam-role-card ${
                          selected ? "is-selected" : ""
                        } ${blocked ? "is-disabled" : ""}`}
                        onClick={() => toggleRole(role.key)}
                      >
                        <span>
                          <strong>{role.label}</strong>
                          <small>{role.description}</small>
                          {role.internalOnly ? (
                            <em>@nexterzone.com only</em>
                          ) : (
                            <em>External emails allowed</em>
                          )}
                        </span>
                        <b>{selected ? "✓" : blocked ? "—" : "+"}</b>
                      </button>
                    );
                  })}
                </div>
              </Section>

              <Section
                eyebrow="Assignment"
                title="Staff / Partner Profile"
                count={isPartnerMode ? "Partner" : form.staffProfile.inspectorType || "None"}
                open={openSection === "staff"}
                onToggle={() =>
                  setOpenSection(openSection === "staff" ? "" : "staff")
                }
              >
                <div className="uam-staff-grid">
                  <div className="uam-field">
                    <label className="uam-label">Inspector Type</label>
                    <select
                      className="uam-input"
                      value={form.staffProfile.inspectorType || ""}
                      onChange={(e) => {
                        const inspectorType = e.target.value || null;

                        if (inspectorType === "internal" && !selectedIsInternal) {
                          setStatus(
                            "Internal inspector requires an @nexterzone.com email."
                          );
                          return;
                        }

                        setForm((prev) => ({
                          ...prev,
                          roles:
                            inspectorType === "partner"
                              ? ["partner_inspector"]
                              : inspectorType === "internal"
                              ? ["inspector"]
                              : prev.roles.filter(
                                  (r) =>
                                    r !== "inspector" &&
                                    r !== "partner_inspector"
                                ),
                          permissions: {
                            ...prev.permissions,
                            inspectionReview:
                              inspectorType === "partner" ||
                              inspectorType === "internal"
                                ? true
                                : prev.permissions.inspectionReview,
                          },
                          staffProfile: {
                            ...prev.staffProfile,
                            inspectorType,
                            organizationType:
                              inspectorType === "partner"
                                ? "partner"
                                : inspectorType === "internal"
                                ? "internal"
                                : "",
                            partnerId:
                              inspectorType === "partner"
                                ? prev.staffProfile.partnerId
                                : "",
                            storeId:
                              inspectorType === "partner"
                                ? prev.staffProfile.storeId
                                : "",
                          },
                        }));
                      }}
                    >
                      <option value="">None</option>
                      <option value="internal" disabled={!selectedIsInternal}>
                        Internal
                      </option>
                      <option value="partner">Partner</option>
                    </select>
                  </div>

                  <div className="uam-field">
                    <label className="uam-label">Partner</label>
                    {partnerOptions.length ? (
                      <select
                        className="uam-input"
                        value={form.staffProfile.partnerId}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            staffProfile: {
                              ...prev.staffProfile,
                              partnerId: e.target.value,
                              storeId: "",
                            },
                          }))
                        }
                        disabled={lookupLoading || form.staffProfile.inspectorType !== "partner"}
                      >
                        <option value="">
                          {lookupLoading ? "Loading partners..." : "Select partner"}
                        </option>

                        {partnerOptions.map((partner) => (
                          <option key={partner.id} value={partner.id}>
                            {partner.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="uam-input"
                        value={form.staffProfile.partnerId}
                        placeholder="Enter partner ID"
                        disabled={form.staffProfile.inspectorType !== "partner"}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            staffProfile: {
                              ...prev.staffProfile,
                              partnerId: e.target.value,
                              storeId: "",
                            },
                          }))
                        }
                      />
                    )}
                  </div>

                  <div className="uam-field">
                    <label className="uam-label">Store / Drop-off Point</label>
                    {storesForSelectedPartner.length ? (
                      <select
                        className="uam-input"
                        value={form.staffProfile.storeId}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            staffProfile: {
                              ...prev.staffProfile,
                              storeId: e.target.value,
                            },
                          }))
                        }
                        disabled={lookupLoading || form.staffProfile.inspectorType !== "partner"}
                      >
                        <option value="">
                          {lookupLoading ? "Loading stores..." : "Select store"}
                        </option>

                        {storesForSelectedPartner.map((store) => (
                          <option key={store.id} value={store.id}>
                            {store.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="uam-input"
                        value={form.staffProfile.storeId}
                        placeholder="Enter store ID"
                        disabled={form.staffProfile.inspectorType !== "partner"}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            staffProfile: {
                              ...prev.staffProfile,
                              storeId: e.target.value,
                            },
                          }))
                        }
                      />
                    )}

                    {selectedStore?.subtitle ? (
                      <div className="uam-helper">{selectedStore.subtitle}</div>
                    ) : null}
                  </div>
                </div>

                <div className="uam-summary-strip">
                  <span>Inspector: {form.staffProfile.inspectorType || "None"}</span>
                  <span>Partner: {selectedPartner?.label || form.staffProfile.partnerId || "—"}</span>
                  <span>Store: {selectedStore?.label || form.staffProfile.storeId || "—"}</span>
                </div>
              </Section>

              <Section
                eyebrow="Capability Matrix"
                title="Permissions"
                count={summaryEnabledPermissions.length}
                open={openSection === "permissions"}
                onToggle={() =>
                  setOpenSection(openSection === "permissions" ? "" : "permissions")
                }
              >
                <div className="uam-permission-stack">
                  {permissionGroups.map(([group, perms]) => (
                    <div key={group} className="uam-permission-group">
                      <div className="uam-permission-group-title">{group}</div>

                      {perms.map((perm) => {
                        const selected = !!form.permissions[perm.key];

                        return (
                          <button
                            key={perm.key}
                            type="button"
                            className={`uam-permission-row ${
                              selected ? "is-selected" : ""
                            }`}
                            onClick={() => togglePermission(perm.key)}
                          >
                            <span>
                              <strong>{perm.label}</strong>
                              <small>{perm.description}</small>
                            </span>

                            <b>{selected ? "On" : "Off"}</b>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </Section>

              <Section
                eyebrow="Snapshot"
                title="Live Summary"
                count={summaryEnabledPermissions.length}
                open={openSection === "summary"}
                onToggle={() =>
                  setOpenSection(openSection === "summary" ? "" : "summary")
                }
              >
                <div className="uam-summary-grid-compact">
                  <div>
                    <span>Active</span>
                    <strong>{form.isActive ? "Yes" : "No"}</strong>
                  </div>
                  <div>
                    <span>Roles</span>
                    <strong>{form.roles.length ? form.roles.join(", ") : "None"}</strong>
                  </div>
                  <div>
                    <span>Permissions</span>
                    <strong>
                      {summaryEnabledPermissions.length
                        ? summaryEnabledPermissions.join(", ")
                        : "None"}
                    </strong>
                  </div>
                </div>
              </Section>

              <Section
                eyebrow="Audit"
                title="Access History"
                count={history.length}
                open={openSection === "history"}
                onToggle={() =>
                  setOpenSection(openSection === "history" ? "" : "history")
                }
              >
                <div className="uam-history-actions">
                  <button
                    type="button"
                    className="uam-btn uam-btn--soft"
                    onClick={() => loadHistory(selectedUser.uid)}
                    disabled={historyLoading || !selectedUser?.uid}
                  >
                    {historyLoading ? "Refreshing..." : "Refresh History"}
                  </button>
                </div>

                {historyStatus ? <div className="uam-status">{historyStatus}</div> : null}

                {historyLoading ? (
                  <div className="uam-empty">Loading history...</div>
                ) : history.length === 0 ? (
                  <div className="uam-empty">No access history found.</div>
                ) : (
                  <div className="uam-history-list compact">
                    {history.map((item) => (
                      <div key={item.id} className="uam-history-card compact">
                        <div>
                          <strong>{item.action || "access_profile_update"}</strong>
                          <span>{formatDateTime(item.changedAt)}</span>
                        </div>
                        <small>
                          Changed by: {item.changedByEmail || item.changedByUid || "Unknown"}
                        </small>
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              <div className="uam-sticky-actions">
                <div>
                  <strong>{saveBlockedReason ? "Action needed" : "Ready to save"}</strong>
                  <span>
                    {saveBlockedReason ||
                      "Review the selected user access profile before saving."}
                  </span>
                </div>

                <div className="uam-sticky-buttons">
                  <button
                    type="button"
                    className="uam-btn uam-btn--soft"
                    onClick={resetForm}
                    disabled={!selectedUser || saving}
                  >
                    Reset
                  </button>

                  <button
                    type="submit"
                    className="uam-btn uam-btn--primary"
                    disabled={saving || !!saveBlockedReason}
                  >
                    {saving ? "Saving..." : "Save Access"}
                  </button>
                </div>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
}